/**
 * DEVELOPER DOCUMENTATION
 *
 * Include your custom JavaScript here.
 *
 * The theme Focal has been developed to be easily extensible through the usage of a lot of different JavaScript
 * events, as well as the usage of custom elements (https://developers.google.com/web/fundamentals/web-components/customelements)
 * to easily extend the theme and re-use the theme infrastructure for your own code.
 *
 * The technical documentation is summarized here.
 *
 * ------------------------------------------------------------------------------------------------------------
 * BEING NOTIFIED WHEN A VARIANT HAS CHANGED
 * ------------------------------------------------------------------------------------------------------------
 *
 * This event is fired whenever a the user has changed the variant in a selector. The target get you the form
 * that triggered this event.
 *
 * Example:
 *
 * document.addEventListener('variant:changed', function(event) {
 *   let variant = event.detail.variant; // Gives you access to the whole variant details
 *   let form = event.target;
 * });
 *
 * ------------------------------------------------------------------------------------------------------------
 * MANUALLY CHANGE A VARIANT
 * ------------------------------------------------------------------------------------------------------------
 *
 * You may want to manually change the variant, and let the theme automatically adjust all the selectors. To do
 * that, you can get the DOM element of type "<product-variants>", and call the selectVariant method on it with
 * the variant ID.
 *
 * Example:
 *
 * const productVariantElement = document.querySelector('product-variants');
 * productVariantElement.selectVariant(12345);
 *
 * ------------------------------------------------------------------------------------------------------------
 * BEING NOTIFIED WHEN A NEW VARIANT IS ADDED TO THE CART
 * ------------------------------------------------------------------------------------------------------------
 *
 * This event is fired whenever a variant is added to the cart through a form selector (product page, quick
 * view...). This event DOES NOT include any change done through the cart on an existing variant. For that,
 * please refer to the "cart:updated" event.
 *
 * Example:
 *
 * document.addEventListener('variant:added', function(event) {
 *   var variant = event.detail.variant; // Get the variant that was added
 * });
 *
 * ------------------------------------------------------------------------------------------------------------
 * BEING NOTIFIED WHEN THE CART CONTENT HAS CHANGED
 * ------------------------------------------------------------------------------------------------------------
 *
 * This event is fired whenever the cart content has changed (if the quantity of a variant has changed, if a variant
 * has been removed, if the note has changed...). This event will also be emitted when a new variant has been
 * added (so you will receive both "variant:added" and "cart:updated"). Contrary to the variant:added event,
 * this event will give you the complete details of the cart.
 *
 * Example:
 *
 * document.addEventListener('cart:updated', function(event) {
 *   var cart = event.detail.cart; // Get the updated content of the cart
 * });
 *
 * ------------------------------------------------------------------------------------------------------------
 * REFRESH THE CART/MINI-CART
 * ------------------------------------------------------------------------------------------------------------
 *
 * If you are adding variants to the cart and would like to instruct the theme to re-render the cart, you cart
 * send the cart:refresh event, as shown below:
 *
 * document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', {
 *   bubbles: true
 * }));
 *
 * ------------------------------------------------------------------------------------------------------------
 * USAGE OF CUSTOM ELEMENTS
 * ------------------------------------------------------------------------------------------------------------
 *
 * Our theme makes extensive use of HTML custom elements. Custom elements are an awesome way to extend HTML
 * by creating new elements that carry their own JavaScript for adding new behavior. The theme uses a large
 * number of custom elements, but the two most useful are drawer and popover. Each of those components add
 * a "open" attribute that you can toggle on and off. For instance, let's say you would like to open the cart
 * drawer, whose id is "mini-cart", you simply need to retrieve it and set its "open" attribute to true (or
 * false to close it):
 *
 * document.getElementById('mini-cart').open = true;
 *
 * Thanks to the power of custom elements, the theme will take care automagically of trapping focus, maintaining
 * proper accessibility attributes...
 *
 * If you would like to create your own drawer, you can re-use the <drawer-content> content. Here is a simple
 * example:
 *
 * // Make sure you add "aria-controls", "aria-expanded" and "is" HTML attributes to your button:
 * <button type="button" is="toggle-button" aria-controls="id-of-drawer" aria-expanded="false">Open drawer</button>
 *
 * <drawer-content id="id-of-drawer">
 *   Your content
 * </drawer-content>
 *
 * The nice thing with custom elements is that you do not actually need to instantiate JavaScript yourself: this
 * is done automatically as soon as the element is inserted to the DOM.
 *
 * ------------------------------------------------------------------------------------------------------------
 * THEME DEPENDENCIES
 * ------------------------------------------------------------------------------------------------------------
 *
 * While the theme tries to keep outside dependencies as small as possible, the theme still uses third-party code
 * to power some of its features. Here is the list of all dependencies:
 *
 * "vendor.js":
 *
 * The vendor.js contains required dependencies. This file is loaded in parallel of the theme file.
 *
 * - custom-elements polyfill (used for built-in elements on Safari - v1.0.0): https://github.com/ungap/custom-elements
 * - web-animations-polyfill (used for polyfilling WebAnimations on Safari 12, this polyfill will be removed in 1 year - v2.3.2): https://github.com/web-animations/web-animations-js
 * - instant-page (v5.1.0): https://github.com/instantpage/instant.page
 * - tocca (v2.0.9); https://github.com/GianlucaGuarini/Tocca.js/
 * - seamless-scroll-polyfill (v2.0.0): https://github.com/magic-akari/seamless-scroll-polyfill
 *
 * "flickity.js": v2.2.0 (with the "fade" package). Flickity is only loaded on demand if there is a product image
 * carousel on the page. Otherwise it is not loaded.
 *
 * "photoswipe": v4.1.3. PhotoSwipe is only loaded on demand to power the zoom feature on product page. If the zoom
 * feature is disabled, then this script is never loaded.
 */



var currencyFormat = window.themeVariables.settings.currencyCodeEnabled ? window.themeVariables.settings.moneyWithCurrencyFormat : window.themeVariables.settings.moneyFormat

function formatMoney(cents, format = "") {
  if (typeof cents === "string") {
    cents = cents.replace(".", "");
  }
  const placeholderRegex = /\{\{\s*(\w+)\s*\}\}/, formatString = format || window.themeVariables.settings.moneyFormat;
  function defaultTo(value2, defaultValue) {
    return value2 == null || value2 !== value2 ? defaultValue : value2;
  }
  function formatWithDelimiters(number, precision, thousands, decimal) {
    precision = defaultTo(precision, 2);
    thousands = defaultTo(thousands, ",");
    decimal = defaultTo(decimal, ".");
    if (isNaN(number) || number == null) {
      return 0;
    }
    number = (number / 100).toFixed(precision);
    let parts = number.split("."), dollarsAmount = parts[0].replace(/(\d)(?=(\d\d\d)+(?!\d))/g, "$1" + thousands), centsAmount = parts[1] ? decimal + parts[1] : "";
    return dollarsAmount + centsAmount;
  }
  let value = "";
  switch (formatString.match(placeholderRegex)[1]) {
    case "amount":
      value = formatWithDelimiters(cents, 2);
      break;
    case "amount_no_decimals":
      value = formatWithDelimiters(cents, 0);
      break;
    case "amount_with_space_separator":
      value = formatWithDelimiters(cents, 2, " ", ".");
      break;
    case "amount_with_comma_separator":
      value = formatWithDelimiters(cents, 2, ".", ",");
      break;
    case "amount_with_apostrophe_separator":
      value = formatWithDelimiters(cents, 2, "'", ".");
      break;
    case "amount_no_decimals_with_comma_separator":
      value = formatWithDelimiters(cents, 0, ",", ".");
      break;
    case "amount_no_decimals_with_space_separator":
      value = formatWithDelimiters(cents, 0, " ");
      break;
    case "amount_no_decimals_with_apostrophe_separator":
      value = formatWithDelimiters(cents, 0, "'");
      break;
  }
  if (formatString.indexOf("with_comma_separator") !== -1) {
    return formatString.replace(placeholderRegex, value);
  } else {
    return formatString.replace(placeholderRegex, value);
  }
}

var addedItemsCount = 0;
document.addEventListener("DOMContentLoaded", function(event) { 
  var customCollectionClearAll = document.querySelector('.custom-collection-main-image .clear-all-button');
//   var customCollectionItemRemoveButtons = document.querySelectorAll('.custom-collection-item__remove');
//   var customCollectionItemAddAllButton = document.querySelector('.custom-collection-add-all-wrapper');
//   var customCollectionItemAddAllButtonCount = document.querySelector('.custom-collection-add-all-wrapper span');
  
  

  document.addEventListener('click', function(e){
    var el = e.target;
    if(el.classList.contains('custom-collection-item__add') || el.closest('.custom-collection-item__add')) {
      if(!el.classList.contains('custom-collection-item__add')) {
        el = el.closest('.custom-collection-item__add');
      }
      
    

      var customCollectionMainImageWrapper = document.querySelector('.custom-collection-main-image-wrapper');

      var parentEl = el.closest('.custom-collection-item');
      var imgElement = parentEl.querySelector('.custom-collection-main-image-item');
      var variantId = parentEl.querySelector('input[name="id"]').value;

      
      if (imgElement != null) {
        if (!imgElement.classList.contains('added')) {
          var clone = imgElement.cloneNode(true);
          clone.setAttribute('data-variant',variantId);
          if (customCollectionMainImageWrapper != null) {
            customCollectionMainImageWrapper.prepend(clone);
            imgElement.classList.add('added');
            parentEl.classList.add('item-added');

            var totalPrice = document.querySelector('.custom-collection-main-image .total-price');
            var itemPrice = +imgElement.getAttribute('data-item-price');
            var totalPriceValue = +totalPrice.getAttribute('data-price');
            totalPrice.innerHTML = formatMoney(itemPrice + totalPriceValue, currencyFormat)
            totalPrice.setAttribute('data-price', itemPrice + totalPriceValue);
          }
        } else {
          var elId = el.closest('.custom-collection-item').getAttribute('data-product-index');
          var productImageWrapper = document.querySelector('.custom-collection-item .custom-collection-main-image-item[data-index="' + elId + '"]');
          var productImage = document.querySelector('.custom-collection-main-image .custom-collection-main-image-item[data-index="' + elId + '"]');
          el.closest('.custom-collection-item').classList.remove('item-added');
          if (productImageWrapper != null) {
            productImageWrapper.classList.remove('added');
          }
          if (productImage != null) {
            var totalPrice = document.querySelector('.custom-collection-main-image .total-price');
            var itemPrice = +productImage.getAttribute('data-item-price');
            var totalPriceValue = +totalPrice.getAttribute('data-price');
            totalPrice.innerHTML = formatMoney(totalPriceValue - itemPrice, currencyFormat)
            totalPrice.setAttribute('data-price', totalPriceValue - itemPrice);
          
          
            productImage.remove();
          }
        }
      }
      
    }
  });
  
  
  document.addEventListener("click", function(e) {
    if (e.target.closest('.custom-collection-main-image-item-remove-btn')) {
      var elId = e.target.closest('.custom-collection-main-image-item').getAttribute('data-index');
      var productWrapper = document.querySelector('.custom-collection-item[data-product-index="' + elId + '"]');
      var productImage = document.querySelector('.custom-collection-item .custom-collection-main-image-item[data-index="' + elId + '"]');

      if (productWrapper != null) {
        productWrapper.classList.remove('item-added');
      }

      if (productImage != null) {
        productImage.classList.remove('added');
      }

      

            var totalPrice = document.querySelector('.custom-collection-main-image .total-price');
            var itemPrice = +e.target.closest('.custom-collection-main-image-item').getAttribute('data-item-price');
            var totalPriceValue = +totalPrice.getAttribute('data-price');
            totalPrice.innerHTML = formatMoney(totalPriceValue - itemPrice, currencyFormat)
            totalPrice.setAttribute('data-price', totalPriceValue - itemPrice);
      
      
//       if (customCollectionItemAddAllButton != null) {
//         if (addedItemsCount > 0) {
//           customCollectionItemAddAllButton.classList.add('show');
//           customCollectionItemAddAllButtonCount.textContent = addedItemsCount;
//         } else {
//           customCollectionItemAddAllButton.classList.remove('show');
//           customCollectionItemAddAllButtonCount.textContent = addedItemsCount;
//         }
//       }

      e.target.closest('.custom-collection-main-image-item').remove();
      
      addedItemsCount = addedItemsCount - 1;
      
    }
  });
  
  if (customCollectionClearAll != null) {
    customCollectionClearAll.addEventListener("click", function(e) {
      var addedItems = document.querySelectorAll('.custom-collection-main-image .custom-collection-main-image-item');
      for (var i = 0; i < addedItems.length; i++) {
        var elId = addedItems[i].getAttribute('data-index');
        var productWrapper = document.querySelector('.custom-collection-item[data-product-index="' + elId + '"]');
        var productImage = document.querySelector('.custom-collection-item .custom-collection-main-image-item[data-index="' + elId + '"]');

        if (productWrapper != null) {
          productWrapper.classList.remove('item-added');
        }

        if (productImage != null) {
          productImage.classList.remove('added');
        }

        addedItems[i].remove();
      }
    });
  }
  
//   for (var i = 0; i < customCollectionItemRemoveButtons.length; i++) {
//     customCollectionItemRemoveButtons[i].addEventListener("click", function(e) {
//       var elId = e.target.closest('.custom-collection-item').getAttribute('data-product-index');
//       var productImageWrapper = document.querySelector('.custom-collection-item .custom-collection-main-image-item[data-index="' + elId + '"]');
//       var productImage = document.querySelector('.custom-collection-main-image .custom-collection-main-image-item[data-index="' + elId + '"]');
//       e.target.closest('.custom-collection-item').classList.remove('item-added');
//       if (productImageWrapper != null) {
//         productImageWrapper.classList.remove('added');
//       }

//       if (productImage != null) {
//         productImage.remove();
//       }
      
//       addedItemsCount = addedItemsCount - 1;

//       if (customCollectionItemAddAllButton != null) {
//         if (addedItemsCount > 0) {
//           customCollectionItemAddAllButton.classList.add('show');
//           customCollectionItemAddAllButtonCount.textContent = addedItemsCount;
//         } else {
//           customCollectionItemAddAllButton.classList.remove('show');
//           customCollectionItemAddAllButtonCount.textContent = addedItemsCount;
//         }
//       }
//     });
//   }
  
});

$( function() {
  $( ".custom-collection-main-image-wrapper" ).sortable({
    axis: "y",
    containment: "parent",
    tolerance: "pointer"
  });
} );

function customCollectionAddAllItems(array){
  var queue = [];
  var newArray = array.split(',');
  for (var i = 0; i < newArray.length; i++) {
    var product = newArray[i]
    queue.push({
      variantId: product,
    });
  }
  var moveAlong = function() {
    // If we still have requests in the queue, let's process the next one.
    if (queue.length) {
      var request = queue.shift();
      var data = 'id='+ request.variantId + '&quantity=1'
      $.ajax({
        type: 'POST',
        url: '/cart/add.js',
        dataType: 'json',
        data: data,
        success: function(res){
          moveAlong();
        },
        error: function(){
          // if it's not last one Move Along else update the cart number with the current quantity
          if (queue.length){
            moveAlong()
          } else {

          }
        }
      });
    }
    // If the queue is empty, we add 1 to cart
    else {    
      var cartContents = fetch(window.Shopify.routes.root + 'cart.js')
      .then(response => response.json())
      .then(data => { 
        document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', {
          bubbles: true,
          detail: {
            cart: data 
          }   
        }));
        document.getElementById('mini-cart').open = true;
      });
      
      $('.custom-collection-main-image .custom-collection-main-image-item').remove();
      $('.custom-collection-main-list .custom-collection-item').removeClass('item-added');
      $('.custom-collection-main-list .custom-collection-main-image-item').removeClass('added');
      $('.custom-collection-add-all-wrapper').removeClass('show');
      addedItemsCount = 0;
    }
  };
  moveAlong();
};

$('.custom-collection-add-all-btn').on('click',function() {
  var customCollectionAddedItemsVariantsIds = '';
  var customCollectionAddedItems = $('.custom-collection-main-image .custom-collection-main-image-item');
  for (var i = 0; i < customCollectionAddedItems.length; i++) {
    var variantId = $(customCollectionAddedItems[i]).attr('data-variant');
    if (i + 1 == customCollectionAddedItems.length) {
      customCollectionAddedItemsVariantsIds = customCollectionAddedItemsVariantsIds + variantId;
    } else {
      customCollectionAddedItemsVariantsIds = customCollectionAddedItemsVariantsIds + variantId + ',';
    }
  }

  customCollectionAddAllItems(customCollectionAddedItemsVariantsIds)
})

function getElementHeight(el) {
  var clone           = el.cloneNode(true);
  var width           = el.getBoundingClientRect().width;
  clone.style.cssText = 'position: fixed; top: 0; left: 0; overflow: auto; visibility: hidden; pointer-events: none; height: unset; max-height: unset; width: ' + width + 'px';
  document.body.append(clone);
  var height = clone.getBoundingClientRect().height;
  clone.remove();
  return height;
}

document.addEventListener("DOMContentLoaded", function() {
  if(document.querySelector('.AjaxinateContainer')) {
    var endlessScroll = new Ajaxinate({
      container: '.AjaxinateContainer',
      pagination: '#AjaxinatePagination',
      method: 'click',
      offset: 0,
      callback: function() {      
      
      }

    });
  }

  var productVariantsMetafieldsLoadMore = document.querySelectorAll('.product-technical-data-grid-open-button');
  var productVariantsMetafieldsElements = document.querySelectorAll('.product-technical-data-wrapper');
  if (productVariantsMetafieldsElements.length) {
    productVariantsMetafieldsElements.forEach(function(item){
      if (getElementHeight(item) - 15 > 320) {
        item.classList.add('short');
      }
    });
  }
  
  if (productVariantsMetafieldsLoadMore.length) {
    productVariantsMetafieldsLoadMore.forEach(function(item){
      item.addEventListener("click", function(e) {
        this.parentElement.classList.remove('short');
      });
    })
  }

  $('.product-technical-data-mobile-button').on('click', function() {
    $(this).toggleClass('open');
    $(this).next().toggleClass('open');
  })
});</drawer-content></product-variants>